import React, { useState } from 'react';
import { X, Calendar, Minus, Plus } from 'lucide-react';
import { useLang } from '../../core/lang.jsx';
import LangKeys from '../../core/locales/keys';
import itemService from '../../services/itemService';

const StockUpdateModal = ({ isOpen, onClose, item, isStockIn, onStockUpdated }) => {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState('1');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [price, setPrice] = useState(isStockIn ? (item?.purchase_price || '') : (item?.sale_price || ''));
  const [notes, setNotes] = useState('');

  if (!isOpen || !item) return null;

  const handleUpdate = async () => {
    const qty = parseFloat(quantity);
    if (!qty || qty <= 0) {
      alert("Please enter a valid quantity");
      return;
    }

    setLoading(true);
    try {
      const currentStock = parseFloat(item.opening_stock || 0);
      const newStock = isStockIn ? currentStock + qty : currentStock - qty;

      if (newStock < 0) {
        alert("Stock cannot be negative.");
        setLoading(false);
        return;
      }

      // Payload exactly matching mobile expectations for stock update
      const updatedData = {
        name: item.name,
        item_name: item.name,
        item_type: item.item_type,
        unit: item.unit,
        stock_unit: item.unit,
        sale_price: isStockIn ? parseFloat(item.sale_price || 0) : parseFloat(price || 0),
        purchase_price: isStockIn ? parseFloat(price || 0) : parseFloat(item.purchase_price || 0),
        tax_included: item.tax_included ? 1 : 0,
        hsn_sac_code: item.hsn_sac_code || '',
        gst_rate: item.gst_rate || '',
        opening_stock: newStock,
        low_stock_alert: parseFloat(item.low_stock_alert || 0),
        as_of_date: date,
        item_id: item.id
      };

      // We don't send image_file for stock updates to avoid multipart issues
      const success = await itemService.updateItem(item.id, updatedData);
      
      if (success === true) {
        if (typeof onStockUpdated === 'function') {
          onStockUpdated();
        }
        onClose();
      } else {
        alert("Failed to update: " + (success?.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Stock update error:", err);
      alert("An error occurred: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={{...styles.header, backgroundColor: isStockIn ? '#16A34A' : '#DC2626'}}>
          <h2 style={styles.title}>{isStockIn ? t(LangKeys.stock_in) : t(LangKeys.stock_out)}</h2>
          <button style={styles.closeBtn} onClick={onClose}><X size={20} /></button>
        </div>

        <div style={styles.body}>
          <p style={styles.subtitle}>
            {isStockIn ? t(LangKeys.enter_purchased_quantity) : t(LangKeys.enter_sold_quantity)}
          </p>

          <div style={styles.inputGroup}>
            <label style={styles.label}>{t(LangKeys.quantity)}</label>
            <div style={styles.qtyRow}>
              <button style={styles.qtyBtn} onClick={() => setQuantity(Math.max(1, parseFloat(quantity || 0) - 1).toString())}><Minus size={16} /></button>
              <input 
                type="number" 
                style={styles.qtyInput} 
                value={quantity} 
                onChange={e => setQuantity(e.target.value)}
              />
              <button style={styles.qtyBtn} onClick={() => setQuantity((parseFloat(quantity || 0) + 1).toString())}><Plus size={16} /></button>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>{t(LangKeys.date)}</label>
            <div style={styles.dateInputWrapper}>
              <Calendar size={16} color="#94A3B8" />
              <input 
                type="date" 
                style={styles.dateInput} 
                value={date} 
                onChange={e => setDate(e.target.value)}
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              {isStockIn ? t(LangKeys.purchase_price) : t(LangKeys.sale_price)}
            </label>
            <div style={styles.priceWrapper}>
              <span style={{fontWeight: '700'}}>₹</span>
              <input 
                type="number" 
                style={styles.input} 
                value={price} 
                onChange={e => setPrice(e.target.value)}
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>{t(LangKeys.add_notes_optional)}</label>
            <textarea 
              style={styles.textarea} 
              placeholder={t(LangKeys.add_notes_optional)}
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </div>
        </div>

        <div style={styles.footer}>
          <button 
            style={{...styles.submitBtn, backgroundColor: isStockIn ? '#16A34A' : '#DC2626'}}
            onClick={handleUpdate}
            disabled={loading}
          >
            {loading ? <div className="spinner-small" /> : (isStockIn ? t(LangKeys.stock_in_caps) : t(LangKeys.stock_out_caps))}
          </button>
        </div>
      </div>
      <style>{`
        .spinner-small { width: 20px; height: 20px; border: 2px solid #FFF; border-top: 2px solid transparent; border-radius: 50%; animation: spin 0.8s linear infinite; } 
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const styles = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 },
  modal: { width: '100%', maxWidth: '400px', backgroundColor: '#FFF', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' },
  header: { padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#FFF' },
  title: { fontSize: '18px', fontWeight: '800', margin: 0 },
  closeBtn: { border: 'none', background: 'rgba(255,255,255,0.2)', color: '#FFF', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  body: { padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' },
  subtitle: { fontSize: '14px', color: '#64748B', fontWeight: '600', margin: 0 },
  inputGroup: { flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontSize: '12px', fontWeight: '700', color: '#94A3B8' },
  qtyRow: { display: 'flex', alignItems: 'center', border: '1px solid #E2E8F0', borderRadius: '12px', height: '48px', overflow: 'hidden' },
  qtyBtn: { width: '40px', height: '100%', border: 'none', backgroundColor: '#F8FAFC', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B' },
  qtyInput: { flex: 1, height: '100%', border: 'none', textAlign: 'center', fontSize: '16px', fontWeight: '800', outline: 'none' },
  dateInputWrapper: { display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid #E2E8F0', borderRadius: '12px', height: '48px', padding: '0 8px', backgroundColor: '#FFF' },
  dateInput: { border: 'none', outline: 'none', fontSize: '13px', fontWeight: '700', flex: 1, color: '#1E293B', backgroundColor: 'transparent', width: '100%', minWidth: '0' },
  priceWrapper: { display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #E2E8F0', borderRadius: '12px', height: '48px', padding: '0 16px' },
  input: { flex: 1, border: 'none', outline: 'none', fontSize: '16px', fontWeight: '800' },
  textarea: { minHeight: '80px', padding: '12px', border: '1px solid #E2E8F0', borderRadius: '12px', outline: 'none', fontSize: '14px', fontWeight: '600', resize: 'none' },
  footer: { padding: '20px', borderTop: '1px solid #F1F5F9' },
  submitBtn: { width: '100%', height: '54px', border: 'none', borderRadius: '16px', color: '#FFF', fontSize: '16px', fontWeight: '800', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
};

export default StockUpdateModal;
